import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    useRecordContext,
    useGetOne,
    TopToolbar,
    DeleteButton,
    ListButton,
    Labeled,
    ReferenceField,
} from 'react-admin';
import {
    Box,
    Typography,
    Chip,
    Divider,
    CardContent,
    Stack,
    Paper,
    Card,
} from '@mui/material';
import {
    Task as TaskIcon,
    LocationOn as ZoneIcon,
    Factory as PlantIcon,
    Delete as DeleteIcon,
    List as ListIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import cronstrueWrapper from '@/components/cronstrueWrapper';

// Componente para mostrar la programación cron
const CronScheduleField = ({ source }: { source: string }) => {
    const record = useRecordContext();
    const cronExpression = record?.[source];

    const formattedCron = React.useMemo(() => {
        if (!cronExpression) return "—";
        try {
            const description = cronstrueWrapper.toString(cronExpression);
            return description.replace(/^En /, "").replace(/^A las /, "");
        } catch {
            return "Expresión personalizada";
        }
    }, [cronExpression]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon
                sx={{
                    mr: 1,
                    fontSize: 16,
                    color: 'text.secondary',
                }}
            />
            <Chip
                label={
                    <Typography variant="body2">
                        {formattedCron}
                    </Typography>
                }
                variant="outlined"
                size="small"
                color="primary"
            />
        </Box>
    );
};

// Componente para mostrar la planta con trazado completo
const PlantField = ({ source }: { source: string }) => {
    const record = useRecordContext();
    const zoneId = record?.[source];

    // Obtener la zona
    const { data: zone, isLoading: isLoadingZone, error: zoneError } = useGetOne(
        'zones',
        { id: zoneId },
        { enabled: !!zoneId }
    );

    // Obtiene la planta
    const { data: plant, isLoading: isLoadingPlant, error: plantError } = useGetOne(
        'plants',
        { id: zone?.plantId },
        { enabled: !!zone?.plantId }
    );

    const isLoading = isLoadingZone || isLoadingPlant;
    const hasError = zoneError || plantError;

    if (!zoneId) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <PlantIcon fontSize="small" color="disabled" />
                <span style={{ color: '#999' }}>Sin planta asignada</span>
            </Box>
        );
    }

    if (isLoading) {
        return <Typography variant="body2">Cargando...</Typography>;
    }

    if (hasError || !plant) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <PlantIcon fontSize="small" color="error" />
                <span style={{ color: '#f44336' }}>Error cargando planta</span>
            </Box>
        );
    }

    return (
        <ReferenceField
            source="zoneId"
            reference="zones"
            link={false}
        >
            <ReferenceField
                source="plantId"
                reference="plants"
                link="show"
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <PlantIcon fontSize="small" color="primary" />
                    <TextField source="plantName" />
                </Box>
            </ReferenceField>
        </ReferenceField>
    );
};

//  zona (clicable)
const ZoneField = ({ source }: { source: string }) => {
    const record = useRecordContext();
    const zoneId = record?.[source];

    const { data: zone, isLoading, error } = useGetOne(
        'zones',
        { id: zoneId },
        { enabled: !!zoneId }
    );

    if (!zoneId) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <ZoneIcon fontSize="small" color="disabled" />
                <span style={{ color: '#999' }}>Sin zona asignada</span>
            </Box>
        );
    }

    if (isLoading) {
        return <Typography variant="body2">Cargando...</Typography>;
    }

    if (error || !zone) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <ZoneIcon fontSize="small" color="error" />
                <span style={{ color: '#f44336' }}>Error cargando zona</span>
            </Box>
        );
    }

    return (
        <ReferenceField
            source="zoneId"
            reference="zones"
            link="show"
        >
            <Box display="flex" alignItems="center" gap={1}>
                <ZoneIcon fontSize="small" color="primary" />
                <TextField source="zoneName" />
            </Box>
        </ReferenceField>
    );
};

const TaskShowActions = () => (
    <TopToolbar>
        
        <DeleteButton icon={<DeleteIcon />} />
        <ListButton icon={<ListIcon />} />
    </TopToolbar>
);

const TasksShow = (props: any) => {
    return (
        <Show actions={<TaskShowActions />} {...props}>
            <SimpleShowLayout>
                {/* Header Section */}
                <Box sx={{ padding: 2 }}>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 3,
                            mb: 3,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            borderLeft: 4,
                            borderLeftColor: 'primary.main',
                        }}
                    >
                        <Box display='flex' alignItems='center'>
                            <TaskIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                            <Box>
                                <Typography variant='h5' component='h1' fontWeight='600'>
                                    Task: <TextField source="variableName" emptyText="Sin nombre" />
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Información Básica */}
                        <Card
                            elevation={1}
                            sx={{
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <TaskIcon
                                        sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }}
                                    />
                                    <Typography variant='h6' fontWeight='500'>
                                        Información Básica
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Stack spacing={2}>
                                    <Labeled label='ID de la Task'>
                                        <TextField source="id" />
                                    </Labeled>

                                    <Labeled label='Nombre de la Task'>
                                        <TextField source="variableName" sx={{ fontWeight: "600" }} />
                                    </Labeled>

                                    <Labeled label='Tipo de Cálculo'>
                                        <TextField source="calculationType" emptyText="No definido" />
                                    </Labeled>

                                    <Labeled label='Descripción'>
                                        <TextField source="description" emptyText="Sin descripción" />
                                    </Labeled>

                                    <Labeled label='Estado'>
                                        <Chip
                                            label={<TextField source="isActive" />}
                                            color="success"
                                            size="small"
                                        />
                                    </Labeled>
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Configuración y Relaciones */}
                        <Card
                            elevation={1}
                            sx={{
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <ScheduleIcon
                                        sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }}
                                    />
                                    <Typography variant='h6' fontWeight='500'>
                                        Configuración y Relaciones
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Stack spacing={2}>
                                    <Labeled label='Programación'>
                                        <CronScheduleField source="cronExpression" />
                                    </Labeled>

                                    <Labeled label='Período de Lookback'>
                                        <TextField source="lookbackPeriod" emptyText="—" />
                                    </Labeled>

                                    <Labeled label='Zona'>
                                        <ZoneField source="zoneId" />
                                    </Labeled>

                                    <Labeled label='Planta'>
                                        <PlantField source="zoneId" />
                                    </Labeled>
                                </Stack>

                                {/* Jerarquía Visual */}
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: 2,
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ mb: 1, display: 'block', fontWeight: 500 }}
                                    >
                                        Jerarquía:
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <PlantIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                                        <Typography variant='caption'>Planta</Typography>
                                        <Typography
                                            variant='caption'
                                            sx={{ mx: 0.5, color: 'text.disabled' }}
                                        >
                                            →
                                        </Typography>
                                        <ZoneIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                                        <Typography variant='caption'>Zona</Typography>
                                        <Typography
                                            variant='caption'
                                            sx={{ mx: 0.5, color: 'text.disabled' }}
                                        >
                                            →
                                        </Typography>
                                        <TaskIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                                        <Typography variant='caption' fontWeight='500'>
                                            Task
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </SimpleShowLayout>
        </Show>
    );
};

export default TasksShow;
